/**
 * locales - vue-i18n 인스턴스와 언어 리소스 로더.
 * 도메인별 json 을 자동 수집해 언어 단위로 lazy load 한다. 새 도메인은 파일만 추가하면 잡힌다.
 */
import { createI18n } from 'vue-i18n';

/** 번역이 없을 때 돌아갈 언어. 원문(한국어)이라 항목이 사라지지 않는다. */
export const DEFAULT_LOCALE = 'ko';

/** 지원 언어 = 리소스 폴더 src/locales/{locale}/ 가 있는 것. 여기 있어야 실제로 전환된다. */
export const SUPPORTED_LOCALES = ['ko', 'en'];

/**
 * 선택기에 띄우는 언어 전체(FRM_LOCALE 5종, 화면 기획 순서).
 * SUPPORTED_LOCALES 에 없는 것은 리소스가 없어 고를 수 없다 - 폴더와 위 상수를 추가하면 바로 열린다.
 */
export const LOCALES = [
  { cd: 'ko', labelKey: 'common.locale.ko' },
  { cd: 'en', labelKey: 'common.locale.en' },
  { cd: 'zh-cn', labelKey: 'common.locale.zhCn' },
  { cd: 'zh-tw', labelKey: 'common.locale.zhTw' },
  { cd: 'ja', labelKey: 'common.locale.ja' },
];

// 기본 언어는 번들에 포함(첫 화면 대기 없음), 나머지는 전환 시점에 가져온다.
// glob 은 리터럴 경로만 받으므로 DEFAULT_LOCALE 로 조립할 수 없다. 대신 번들 키를 경로에서 뽑아
// 상수와 어긋나면 개발 중에 드러나게 한다(폴더만 바꾸고 상수를 안 고치면 전 화면이 조용히 한국어가 된다).
const eagerFiles = import.meta.glob('./ko/*.json', { eager: true });
const EAGER_LOCALE = Object.keys(eagerFiles)[0]?.split('/')[1] || DEFAULT_LOCALE;
const lazyFiles = import.meta.glob('./*/*.json');

// 파일명이 곧 최상위 키(도메인). ./en/env.json -> messages.env
function toBundle(entries) {
  const bundle = {};
  for (const [path, mod] of entries) {
    const domain = path.slice(path.lastIndexOf('/') + 1, -'.json'.length);
    bundle[domain] = mod?.default || mod;
  }
  return bundle;
}

export const i18n = createI18n({
  legacy: false,
  locale: DEFAULT_LOCALE,
  fallbackLocale: DEFAULT_LOCALE,
  messages: { [EAGER_LOCALE]: toBundle(Object.entries(eagerFiles)) },
  // 어느 언어에도 없는 키. 폴백으로 넘겨 화면이 비지 않게 하되 개발 중에는 드러낸다.
  // 번역만 빠진 키(ko 만 있음)는 vue-i18n 기본 fallbackWarn 이 콘솔에 알린다.
  missing: (locale, key) => {
    if (import.meta.env.DEV) {
      console.warn(`[i18n] missing: ${locale}.${key}`);
    }
    return undefined;
  },
});

const loaded = new Set([DEFAULT_LOCALE]);

/** 해당 언어의 도메인 파일을 모아 i18n 에 적재한다. 이미 적재됐으면 건너뛴다. */
export async function loadLocaleMessages(locale) {
  if (loaded.has(locale)) return;
  const paths = Object.keys(lazyFiles).filter((p) => p.startsWith(`./${locale}/`));
  const mods = await Promise.all(paths.map((p) => lazyFiles[p]()));
  i18n.global.setLocaleMessage(locale, toBundle(paths.map((p, i) => [p, mods[i]])));
  loaded.add(locale);
}

let localeSeq = 0;

/** 화면 언어 적용. 리소스를 먼저 로드해 반쪽 번역 상태를 만들지 않는다. */
export async function setI18nLocale(locale) {
  const next = SUPPORTED_LOCALES.includes(locale) ? locale : DEFAULT_LOCALE;
  const seq = ++localeSeq;
  await loadLocaleMessages(next);
  // 빠르게 연속으로 고르면 늦게 도착한 lazy import 가 최종 선택을 뒤집는다. 최신 요청만 적용한다.
  if (seq !== localeSeq) {
    return i18n.global.locale.value;
  }
  i18n.global.locale.value = next;
  document.documentElement.setAttribute('lang', next);
  return next;
}
